import React, { Component } from 'react';
import {
  Box,
  Button,
  Container,
  Flashbar,
  FlashbarProps,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
  TokenGroup,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { Redirect } from 'react-router-dom';

import { createRequestForDataPermission, editDataLakeRole, getRequest } from 'src/api/permissions';
import { getPendingDataAccessRequestContent, PENDING_DATA_ACCESS_REQUEST_SUBJECT } from 'src/components/utils/emails';
import { createNotification } from 'src/api/notifications';
import {
  CreateRequestForDataPermissionRequest,
  MatchedTemplateWorkflow,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { Page } from 'src/routes';
import {
  ALL_TABLES_RES_NAME,
  APPROVALS_PRIORITY,
  FGA_POLICY_TYPE_PREFIX,
  FORM_CONTENT_TYPE,
  IAM_ACCESS_ITEM,
  INVALID_PARAMETER_EXCEPTION_NAME,
  LAKE_FORMATION_TAG_ITEM,
  NON_ISENGARD_ACCESS_GLADSTONE_ERROR_PREFIX,
  NON_ISENGARD_ACCESS_WIKI_URL,
  NON_PRODUCTION_ACCESS_GLADSTONE_ERROR_PREFIX,
  NON_PRODUCTION_ACCESS_WIKI_URL,
  REDSHIFT_TAG_ITEM,
  RESOURCE_ITEM,
  TAG_ITEM,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
} from 'src/commons/constants';
import { extractApprovalTemplateIdFromLink } from 'src/components/permissions/myPermissions/createLakeFormationPermissionsPage';
import { convertApprovalWorkflowIDToLink } from 'src/components/permissions/myPermissions/common';
import _ from 'lodash';
import { isValidPrincipalNotRoot } from 'src/commons/validationUtils';
import {
  CartAccessItem,
  getHCResourceIdForCartItem,
  getMatchedTemplatesForResource,
  getResource,
  pairsToMapWithArrayValues,
  TemplateMetadata,
  updateTemplateInfo,
  validateAdditionalMetadataForm,
} from 'src/components/common/checkout/common';
import { CheckoutWizard } from 'src/components/common/checkout/checkoutWizard';

export interface CheckoutProps {
  cartItems: CartAccessItem[];
  setContentType: any;
  activeGroup: string;
  removeFromCart: (items: CartAccessItem[]) => void;
  setItemsInCart: (items: CartAccessItem[]) => void;
  cartItemIds: string[];
  username: string;
  activeWorkspace: any;
  matchedTemplates: MatchedTemplateWorkflow[];
}

export interface CheckoutState {
  redirect: string;
  catalogToItemsMap: Map<string, object[]>;
  requestSending: boolean;
  checkoutSucceeded: boolean;
  notifications: FlashbarProps.MessageDefinition[];
  additionalMetadata: TemplateMetadata[];
  useCase: string;
  dataConsumerType: string;
  formSubmitted: boolean;
  // all templates that matched, might have duplicate templateIds
  matchedTemplates: MatchedTemplateWorkflow[];
  loading: boolean;
}

export const generateApprovalWorkflowMessage = (approvalWorkflowLinks: string[]) => {
  return (
    <div>
      As part of the request process, some approval workflows were created in Amazon Approvals and action is required.
      To finalize the request, navigate to the below links and fill out the remaining details by editing the summary
      under Basic Details and Start the approval.
      <ul>
        {approvalWorkflowLinks?.map((aWT) => {
          return (
            <li>
              <Link color='inverted' href={aWT.valueOf()} external={true}>
                {aWT.valueOf()}{' '}
              </Link>
            </li>
          );
        })}
      </ul>
      You can come back to these links any time by navigating to the New Access Requests page on the sidebar and
      selecting the relevant request under the Outgoing tab.
    </div>
  );
};

export const getErrorMessageContent = (error) => {
  let content = error.message;
  let errMsg = error.message;
  if (INVALID_PARAMETER_EXCEPTION_NAME === error.name) {
    if (errMsg.startsWith(NON_ISENGARD_ACCESS_GLADSTONE_ERROR_PREFIX)) {
      errMsg = errMsg.split(NON_ISENGARD_ACCESS_GLADSTONE_ERROR_PREFIX)[1] + ' is not managed by Isengard. ';
      content = (
        <>
          {errMsg}
          <Link external href={NON_ISENGARD_ACCESS_WIKI_URL}>
            Learn more
          </Link>
        </>
      );
    }
    if (errMsg.startsWith(NON_PRODUCTION_ACCESS_GLADSTONE_ERROR_PREFIX)) {
      errMsg = errMsg.split(NON_PRODUCTION_ACCESS_GLADSTONE_ERROR_PREFIX)[1] + ' is not a production account. ';
      content = (
        <>
          {errMsg}
          <Link external href={NON_PRODUCTION_ACCESS_WIKI_URL}>
            Learn more
          </Link>
        </>
      );
    }
  }
  return content;
};

export class Checkout extends Component<CheckoutProps, CheckoutState> {
  state = {
    redirect: undefined,
    catalogToItemsMap: new Map(),
    requestSending: false,
    checkoutSucceeded: false,
    notifications: [],
    additionalMetadata: [],
    useCase: '',
    dataConsumerType: 'DataConsumer',
    formSubmitted: false,
    matchedTemplates: [],
    loading: false,
  };

  MAX_DATASETS_PER_REQUEST = 500;

  componentDidMount = async () => {
    this.props.setContentType(FORM_CONTENT_TYPE);
    this.submitAccessRequest = this.submitAccessRequest.bind(this);
    this.sendNotificationToDataProviders = this.sendNotificationToDataProviders.bind(this);
    this.handleAccessRequest = this.handleAccessRequest.bind(this);
    this.setState({ loading: true });
    this.setCatalogToItemsMap();
    await this.getAllTemplateInfo();
    this.setState({ loading: false });
  };

  componentDidUpdate = async (prevProps) => {
    if (prevProps.cartItemIds.length != this.props.cartItemIds.length) {
      this.setState({ loading: true });
      this.setCatalogToItemsMap();
      await this.getAllTemplateInfo();
      this.setState({ loading: false });
    }
  };

  getAllTemplateInfo = async () => {
    let templateMetadataList: TemplateMetadata[] = [];
    for (let item of this.props.cartItems) {
      const cartItemResourceId = getHCResourceIdForCartItem(item);
      const templates = await updateTemplateInfo(
        cartItemResourceId,
        this.props.activeWorkspace,
        this.props.username,
        (matchedTemplates) => this.setState({ matchedTemplates }),
      );
      templateMetadataList = templateMetadataList.concat(templates);
    }

    //Group by template ID
    const groupByTemplId = _.groupBy(templateMetadataList, 'templateId');
    //Removed the duplicates based on templateId
    const uniqueTemplates = _.uniqBy(templateMetadataList, 'templateId');
    //Keep track of cart item resource IDs that share the same template and map to its template resource ID
    uniqueTemplates.forEach((uniqTempl) => {
      const hcIdTempResIdPairs: [string, any][] = groupByTemplId[uniqTempl.templateId].map((templ) => [
        templ.id,
        templ.resourceId,
      ]);
      uniqTempl['cartItemResIdToTemplResIdMap'] = pairsToMapWithArrayValues(hcIdTempResIdPairs);
    });
    console.log('Updating state additional metadata with:', uniqueTemplates);
    this.setState({
      additionalMetadata: uniqueTemplates,
    });
  };

  // We are storing a catalog -> itemsInCatalog map in the state.
  // This populates that map.
  setCatalogToItemsMap() {
    const catalogToItemsMap = new Map();
    for (const item of this.props.cartItems) {
      const catalogName = item['catalogDisplayName'] ? item['catalogDisplayName'] : item['catalogName'];
      if (!catalogToItemsMap.has(catalogName)) {
        catalogToItemsMap.set(catalogName, []);
      }
      catalogToItemsMap.get(catalogName).push(item);
    }
    this.setState({
      catalogToItemsMap: catalogToItemsMap,
    });
  }

  // Retrieve token groups, split up by catalogs (if there are multiple)
  getTokenGroupsByCatalog() {
    const tokenGroups = [];
    // Sorting the catalogs
    const sortedCatalogs = Array.from(this.state.catalogToItemsMap.keys()).sort();
    for (const catalog of sortedCatalogs) {
      tokenGroups.push(this.getTokenGroupForItems(this.state.catalogToItemsMap.get(catalog), catalog));
    }
    return <div>{tokenGroups}</div>;
  }

  // Converting items into something more helpful for token groups
  convertItemsForTokenGroup(items: object[]) {
    const toReturn = [];
    // Can't use iterator because we need to maintain order
    for (const item of items) {
      if (item['itemType'] == undefined) {
        toReturn.push({
          label: item['tableName'],
          labelTag: item['databaseName'],
          dismissLabel: 'Remove ' + item['tableName'],
          description: `This table is owned by ${item['owners'].join(', ')}`,
          key: item['id'],
        });
      } else if (item['itemType'] == REDSHIFT_TAG_ITEM || item['itemType'] == LAKE_FORMATION_TAG_ITEM) {
        let res = {
          label: item['tag'],
          tags: item['resources'],
          description: `This tag is owned by ${item['owner']}`,
          dismissLabel: 'Remove ' + item['tag'],
          itemType: TAG_ITEM,
          ownerWorkspace: item['owner'],
          dataOwnerRole: item['dataOwnerRole'],
          catalogId: item['catalogId'],
          catalogName: item['catalogName'],
          tagKey: item['tagKey'],
          tagValue: item['tagValue'],
          permissionId: item['permissionId'],
        };
        toReturn.push(res);
      } else if (item['itemType'] == RESOURCE_ITEM) {
        let res = {
          label: item['datasetName'],
          labelTag: item['databaseName'],
          dismissLabel: 'Remove ' + item['datasetName'],
          description: `This table is owned by ${item['owner']}`,
          // key: item['id'],
          itemType: RESOURCE_ITEM,
          ownerWorkspace: item['owner'],
          catalogId: item['catalogId'],
          catalogName: item['catalogName'],
          databaseName: item['databaseName'],
          permissionId: item['permissionId'],
          dataOwnerRole: item['dataOwnerRole'],
        };
        toReturn.push(res);
      } else if (item['itemType'] == FGA_POLICY_TYPE_PREFIX) {
        let res = {
          label: item['fgapName'],
          dismissLabel: 'Remove ' + item['tableName'],
          description: `This policy is owned by ${item['owner']}`,
          key: item['id'],
          itemType: FGA_POLICY_TYPE_PREFIX,
          ownerWorkspace: item['owner'],
          catalogId: item['catalogId'],
          clusterId: item['clusterId'],
          catalogName: item['catalogName'],
          databaseName: item['databaseName'],
          schemaName: item['schemaName'],
          tableName: item['tableName'],
          permissionId: item['permissionId'],
          fgapName: item['fgapName'],
          fgapId: item['id'],
          dataOwnerRole: item['dataOwnerRole'],
        };
        toReturn.push(res);
      }
    }
    return toReturn;
  }

  // Retrieve the token group structure for items of the same catalog
  getTokenGroupForItems(items, catalog) {
    const convertedItems = this.convertItemsForTokenGroup(items);

    return (
      <Box key={catalog}>
        <Header
          variant={'h3'}
          counter={convertedItems.length !== this.props.cartItems.length ? `(${convertedItems.length})` : undefined}
        >
          Datasets and tags in catalog: {catalog}
        </Header>
        <FormField>
          <TokenGroup
            alignment='vertical'
            items={convertedItems}
            onDismiss={({ detail: { itemIndex } }) => {
              const removedDataSet = items[itemIndex];
              this.props.removeFromCart([removedDataSet]);
            }}
          />
        </FormField>
      </Box>
    );
  }

  convertNeededOnDate = (oldDate: string) => {
    const arr = oldDate.split('-');
    return arr[1] + '/' + arr[2] + '/' + arr[0];
  };

  mapMetadata(accessManagementTemplateList): any[] {
    return accessManagementTemplateList.map((item) => ({
      templateId: item.templateId,
      resourceId: item.resourceId,
      simTicketLinks: item['simTicketLinks'],
      approvalTemplateId: item['approvalTemplateId'],
      userAlias: item['userAlias'],
      neededOnDate: item['neededOnDate'],
      priority: item['priority'],
      allowMergeApprovalWorkflow: item['allowMergeApprovalWorkflow'],
      autoApproval: item['autoApproval'],
      // AccessRequestWorkflow metadata
      templateType: item['templateType'],
      agreementId: item['agreementId'],
      agreementType: item['agreementType'],
      requestContextId: item['requestContextId'],
    }));
  }

  mapMetadataForCartItem(accessManagementTemplateList, cartItem, templateType: string): any[] {
    const mappedMetadata = [];
    const cartItemResourceId = getHCResourceIdForCartItem(cartItem);
    accessManagementTemplateList.forEach((item: TemplateMetadata) => {
      if (!_.isEmpty(item.cartItemResIdToTemplResIdMap) && cartItemResourceId in item.cartItemResIdToTemplResIdMap) {
        // there might be more than one template/resource Id assigned
        item.cartItemResIdToTemplResIdMap[cartItemResourceId].forEach((tempResId) => {
          let metadata: any = {
            templateId: item.templateId,
            resourceId: tempResId,
            approvalTemplateId: item['approvalTemplateId'],
            userAlias: item['userAlias'],
            neededOnDate: item['neededOnDate'],
            priority: item['priority'],
            allowMergeApprovalWorkflow: item['allowMergeApprovalWorkflow'],
            autoApproval: item['autoApproval'],
          };
          // SIM tickets are only for AM templates
          if (templateType == TEMPLATE_TYPE_ACCESS_MANAGEMENT) {
            metadata['simTicketLinks'] = item['simTicketLinks'];
          }
          // DUA and template type itself are only for ARW templates
          else if (templateType == TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW) {
            metadata.templateType = item['templateType'];
            metadata.agreementId = item['agreementId'];
            metadata.agreementType = item['agreementType'];
            metadata.requestContextId = item['requestContextId'];
          }

          mappedMetadata.push(metadata);
        });
      }
    });
    return mappedMetadata;
  }

  // Submitting request for access to items in the cart
  async submitAccessRequest() {
    const failMessage = 'Failed to submit the access request';
    const successMessage = `Successfully submitted the access request. This will remain pending until the data owners approve or deny the request.`;
    const numCartItems = this.props.cartItemIds.length;
    let createdRequestIds = [];
    try {
      let valueArr = [];
      for (let curArr of Array.from(this.state.catalogToItemsMap.values())) {
        valueArr.push(...curArr);
      }
      // verify checkout data type(Omni or Foundry data)
      let type = valueArr[0].itemType;
      if (type == undefined) {
        for (let i = 0; i < numCartItems; i++) {
          if (valueArr[i].itemType !== undefined) {
            this.setState({
              notifications: [
                ...this.state.notifications,
                {
                  type: 'error',
                  header: failMessage,
                  content: 'Can only checkout Foundry or Omni data.',
                  dismissible: true,
                  onDismiss: () => this.dismissNotification('Can only checkout Foundry or Omni data.'),
                },
              ],
            });
            return;
          }
        }
      } else {
        for (let i = 0; i < numCartItems; i++) {
          if (valueArr[i].itemType == undefined) {
            this.setState({
              notifications: [
                ...this.state.notifications,
                {
                  type: 'error',
                  header: failMessage,
                  content: 'Can only checkout Foundry or Omni data.',
                  dismissible: true,
                  onDismiss: () => this.dismissNotification('Can only checkout Foundry or Omni data.'),
                },
              ],
            });
            return;
          }
        }
      }
      if (type == undefined || type == IAM_ACCESS_ITEM) {
        // IAM access type
        for (let i = 0; i < numCartItems; i += this.MAX_DATASETS_PER_REQUEST) {
          const endIndex = Math.min(i + this.MAX_DATASETS_PER_REQUEST, numCartItems);
          const accessManagementTemplateMetadata = this.getAccessManagementInfoForRequest(
            TEMPLATE_TYPE_ACCESS_MANAGEMENT,
          );
          const accessManagementTemplateMetadataInfo = this.mapMetadata(accessManagementTemplateMetadata);
          const response = await editDataLakeRole({
            groupId: this.props.activeGroup,
            datasetsToAdd: this.props.cartItemIds.slice(i, endIndex),
            accessManagementTemplateMetadata: accessManagementTemplateMetadataInfo,
          });
          createdRequestIds = createdRequestIds.concat(response.addDatasetsRequestIds);
        }
      } else {
        for (let i = 0; i < numCartItems; i++) {
          const cartItem = valueArr[i];
          const accessManagementTemplateMetadata = this.getAccessManagementInfoForRequest(
            TEMPLATE_TYPE_ACCESS_MANAGEMENT,
          );
          const accessRWTemplateMetadata = this.getAccessManagementInfoForRequest(
            TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
          );
          // separate requests for each item, so we filter it here
          const mappedMetadataForAMTemplate = this.mapMetadataForCartItem(
            accessManagementTemplateMetadata,
            cartItem,
            TEMPLATE_TYPE_ACCESS_MANAGEMENT,
          );
          const mappedMetadataForARWTemplate = this.mapMetadataForCartItem(
            accessRWTemplateMetadata,
            cartItem,
            TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
          );
          const baseRequest = {
            ownerRequestedBy: this.props.activeWorkspace.workspaceId,
            ownerRequestedTo: cartItem.owner,
            dataPermissionPublisherRole: cartItem.dataOwnerRole,
            dataPermissionId: cartItem.permissionId,
            dataLocationRegion: cartItem.region,
            useCase: this.state.useCase,
            dataConsumerType: this.state.dataConsumerType,
            dataPermissionConsumerRole: this.props.activeWorkspace.workspaceRoleArn,
            permissions: ['SELECT'],
            permissionsWithGrantOption: isValidPrincipalNotRoot(cartItem.dataLakePrincipal) ? [] : ['SELECT'],
            dataLakePrincipal: cartItem.dataLakePrincipal,
          };
          const resource = getResource(cartItem);
          console.log('Populated access request resource object: ', resource);
          if (cartItem.itemType == RESOURCE_ITEM) {
            if (cartItem.datasetName !== ALL_TABLES_RES_NAME) {
              // table-level access
              // DataZone table-level
              const isDataZone =
                cartItem.dataZoneAssetId !== undefined && cartItem.dataZoneConsumerProjectId !== undefined;
              let finalRequest: CreateRequestForDataPermissionRequest = {
                ...baseRequest,
                resource,
                accessManagementTemplateMetadata: mappedMetadataForAMTemplate,
                accessRequestWorkflowMetadata: mappedMetadataForARWTemplate,
              };

              if (isDataZone) {
                // remove these unneeded fields from base request for DZ
                delete finalRequest.dataPermissionId;
                delete finalRequest.permissions;
                delete finalRequest.permissionsWithGrantOption;
                delete finalRequest.dataLakePrincipal;
                finalRequest.dataZoneConsumerProjectId = cartItem.dataZoneConsumerProjectId;
                finalRequest.dataZoneAssetId = cartItem.dataZoneAssetId;
              }

              const response = await createRequestForDataPermission(finalRequest);
              createdRequestIds.push(response.requestId);
            } else {
              // All Tables - level access
              const response = await createRequestForDataPermission({
                ...baseRequest,
                resource,
                accessManagementTemplateMetadata: mappedMetadataForAMTemplate,
                accessRequestWorkflowMetadata: mappedMetadataForARWTemplate,
              });
              createdRequestIds.push(response.requestId);
            }
          } else if (cartItem.itemType == LAKE_FORMATION_TAG_ITEM) {
            const response = await createRequestForDataPermission({
              ...baseRequest,
              resource,
              accessManagementTemplateMetadata: mappedMetadataForAMTemplate,
              accessRequestWorkflowMetadata: mappedMetadataForARWTemplate,
            });
            createdRequestIds.push(response.requestId);
          } else if (cartItem.itemType == REDSHIFT_TAG_ITEM) {
            const response = await createRequestForDataPermission({
              ...baseRequest,
              resource,
              accessManagementTemplateMetadata: mappedMetadataForAMTemplate,
              accessRequestWorkflowMetadata: mappedMetadataForARWTemplate,
            });
            createdRequestIds.push(response.requestId);
          } else if (cartItem.itemType == FGA_POLICY_TYPE_PREFIX) {
            const response = await createRequestForDataPermission({
              ...baseRequest,
              resource,
              accessManagementTemplateMetadata: mappedMetadataForAMTemplate,
              accessRequestWorkflowMetadata: mappedMetadataForARWTemplate,
            });
            createdRequestIds.push(response.requestId);
          }
        }
      }
    } catch (err) {
      console.log(err);
      const errMsgContent = getErrorMessageContent(err);
      this.setState({
        notifications: [
          ...this.state.notifications,
          {
            type: 'error',
            header: failMessage,
            content: errMsgContent,
            dismissible: true,
            onDismiss: () => this.dismissNotification(errMsgContent),
          },
        ],
      });
      return;
    }
    console.log(`Created access requestIds: ${createdRequestIds}`);
    let approvalWorkflowLinks = [];
    for (const requestId of createdRequestIds) {
      const getRequestResponse = await getRequest({ requestId: requestId });
      const accessManagementInfoList = getRequestResponse?.requestItem?.additionalMetadata?.accessManagementInfoList;
      if (accessManagementInfoList) {
        accessManagementInfoList.forEach((aMIS) => {
          if (aMIS.approvalWorkflowId) {
            const approvalWorkflowLink = convertApprovalWorkflowIDToLink(aMIS.approvalWorkflowId);
            approvalWorkflowLinks.push(approvalWorkflowLink);
          }
        });
      }
    }

    let newNotifications = [
      ...this.state.notifications,
      {
        type: 'success',
        content: successMessage,
        dismissible: true,
        onDismiss: () => this.dismissNotification(successMessage),
      },
    ];
    if (approvalWorkflowLinks.length > 0) {
      const approvalWorkflowMessage = generateApprovalWorkflowMessage(approvalWorkflowLinks);
      newNotifications.push({
        type: 'info',
        content: approvalWorkflowMessage,
        dismissible: true,
        onDismiss: () => this.dismissNotification(approvalWorkflowMessage),
      });
    }
    this.setState({
      notifications: newNotifications,
      checkoutSucceeded: true,
    });
  }

  getAccessManagementInfoForRequest(templateType: string): any[] {
    return this.state.additionalMetadata
      .filter((tempMetadata) => tempMetadata.templateType == templateType)
      .map((template) => {
        let accessManagementRequest = {
          templateId: template.templateId,
          resourceId: template.resourceId,
          templateType: template.templateType,
          cartItemResIdToTemplResIdMap: template.cartItemResIdToTemplResIdMap,
        };
        if (template.templateType == TEMPLATE_TYPE_ACCESS_MANAGEMENT) {
          if (template.accessManagementInfo?.simTemplates) {
            let simTickets = template.accessManagementInfo.simTemplates.map((simTemplate) => {
              return {
                simTemplateLink: simTemplate.simTemplateLink,
                simTemplateName: simTemplate.simTemplateName,
                simTicketLink: simTemplate.simTicketLink,
              };
            });
            accessManagementRequest['simTicketLinks'] = simTickets;
          }
          const approvalsWorkflowTemplateInfo = template.accessManagementInfo?.approvalsWorkflowTemplate;
          if (approvalsWorkflowTemplateInfo) {
            accessManagementRequest['approvalTemplateId'] = approvalsWorkflowTemplateInfo.approvalTemplateId;
            accessManagementRequest['userAlias'] = approvalsWorkflowTemplateInfo.userAlias;
            accessManagementRequest['neededOnDate'] = this.convertNeededOnDate(
              approvalsWorkflowTemplateInfo.neededOnDate,
            );
            accessManagementRequest['priority'] = approvalsWorkflowTemplateInfo.priority;
            accessManagementRequest['allowMergeApprovalWorkflow'] =
              approvalsWorkflowTemplateInfo.allowMergeApprovalWorkflow;
            accessManagementRequest['autoApproval'] = approvalsWorkflowTemplateInfo.autoApproval;
          }
        } else if (template.templateType == TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW) {
          // only a single ARW per template.
          const approvalsWorkflowTemplateInfo0 = template.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate;
          if (approvalsWorkflowTemplateInfo0) {
            accessManagementRequest['approvalTemplateId'] = approvalsWorkflowTemplateInfo0.approvalTemplateId;
            accessManagementRequest['userAlias'] = approvalsWorkflowTemplateInfo0.userAlias;
            accessManagementRequest['neededOnDate'] = this.convertNeededOnDate(
              approvalsWorkflowTemplateInfo0.neededOnDate,
            );
            accessManagementRequest['priority'] = approvalsWorkflowTemplateInfo0.priority;
            accessManagementRequest['allowMergeApprovalWorkflow'] =
              approvalsWorkflowTemplateInfo0.allowMergeApprovalWorkflow;
            accessManagementRequest['autoApproval'] = approvalsWorkflowTemplateInfo0.autoApproval;
          }
          const dataAgreement0 = template.accessRequestWorkflows?.[0]?.dataAgreement;
          if (dataAgreement0 && dataAgreement0.agreementId) {
            accessManagementRequest['agreementId'] = dataAgreement0.agreementId;
            accessManagementRequest['agreementType'] = dataAgreement0.agreementType;
          }
          accessManagementRequest['requestContextId'] = template.accessRequestWorkflows?.[0]?.requestContextId;
        }
        return accessManagementRequest;
      });
  }

  // Sends a notification telling the data provider they have pending access requests
  async sendNotificationToDataProviders() {
    function failMessage(provider) {
      return `Failed to send a notification to the data provider, ${provider}, about your access request.`;
    }

    function successMessage(provider) {
      return `Successfully sent a notification to the data provider, ${provider}, about your access request.`;
    }

    const dataProviderToDatasets = new Map<string, object[]>();
    for (const item of this.props.cartItems) {
      if (item['primaryOwner'] != undefined) {
        const currentNotificationRecipientGroup = item['primaryOwner'];
        if (!dataProviderToDatasets.has(currentNotificationRecipientGroup)) {
          dataProviderToDatasets.set(currentNotificationRecipientGroup, []);
        }
        let currentItems = dataProviderToDatasets.get(currentNotificationRecipientGroup);
        currentItems.push(item);
      }
    }

    for (const dataProviderRecipient of dataProviderToDatasets.keys()) {
      const items = dataProviderToDatasets.get(dataProviderRecipient);
      try {
        await createNotification({
          SenderId: this.props.activeGroup,
          ReceiverIdList: [dataProviderRecipient],
          PriorityLevel: 'MEDIUM',
          Subject: PENDING_DATA_ACCESS_REQUEST_SUBJECT,
          Content: getPendingDataAccessRequestContent(items, this.props.activeGroup, dataProviderRecipient),
          EmailNeeded: true,
        });
      } catch (err) {
        this.setState({
          notifications: [
            ...this.state.notifications,
            {
              type: 'error',
              content: failMessage(dataProviderRecipient),
              dismissible: true,
              onDismiss: () => this.dismissNotification(failMessage(dataProviderRecipient)),
            },
          ],
        });
        return;
      }
      this.setState({
        notifications: [
          ...this.state.notifications,
          {
            type: 'success',
            content: successMessage(dataProviderRecipient),
            dismissible: true,
            onDismiss: () => this.dismissNotification(successMessage(dataProviderRecipient)),
          },
        ],
      });
    }
  }

  // Submits access requests and send notification of the access request to the data provider
  async handleAccessRequest() {
    const isDataIncomplete = validateAdditionalMetadataForm(this.state.additionalMetadata);
    if (isDataIncomplete) {
      this.setNotification('error', 'Some required fields are not yet complete! Complete and try again!');
      return;
    }
    if (this.props.cartItems.length === 0) {
      this.setNotification('error', 'No access requests to submit!');
      return;
    }
    // Do not submit form if use case is not provided.
    if (!this.state.useCase) {
      this.setState({ formSubmitted: true });
      return;
    } else {
      this.setState({ formSubmitted: false });
    }

    this.setState({ requestSending: true });
    await this.submitAccessRequest();
    if (this.state.checkoutSucceeded) {
      await this.sendNotificationToDataProviders();
      // Remove all items from the cart
      this.props.setItemsInCart([]);
    }
    this.setState({ requestSending: false });
  }

  private setNotification(type: string, contentStr: string) {
    this.setState({
      notifications: [
        ...this.state.notifications,
        {
          type: type,
          content: contentStr,
          dismissible: true,
          onDismiss: () => this.dismissNotification(contentStr),
        },
      ],
    });
  }

  dismissNotification(messageString) {
    this.setState({
      notifications: this.state.notifications.filter((notificationItem) => notificationItem.content !== messageString),
    });
  }

  render() {
    if (this.state.redirect) {
      return <Redirect push to={this.state.redirect} />;
    }

    if (!this.state.catalogToItemsMap) {
      return (
        <>
          <Flashbar items={this.state.notifications} />
          <Spinner size='large' />;
        </>
      );
    }

    return (
      <>
        <Flashbar items={this.state.notifications} />
        <SpaceBetween size={'l'} direction={'vertical'}>
          <Header
            variant='h1'
            description='Please review the resources in your cart and fill out the form below. Then, select "Request access" to submit a request to the data provider team,
             and they can approve or deny the request.'
          >
            Request access
          </Header>
          <Container
            header={
              <Header variant='h2' counter={`(${this.props.cartItems.length})`}>
                Items in cart
              </Header>
            }
          >
            {this.props.cartItems.length === 0 ? (
              <div>
                <Button variant={'link'} onClick={() => this.setState({ redirect: Page.SEARCH })}>
                  You have no items in your cart.
                </Button>
              </div>
            ) : (
              this.getTokenGroupsByCatalog()
            )}
          </Container>
          {this.state.loading && <Spinner />}
          {!this.state.loading && (
            <CheckoutWizard
              {...this.props}
              cartItems={this.props.cartItems}
              additionalMetadata={this.state.additionalMetadata}
              setAdditionalMetadata={(metadata) => {
                this.setState({ additionalMetadata: metadata });
              }}
              matchedTemplates={this.state.matchedTemplates}
              useCase={this.state.useCase}
              setUseCase={(useCaseValue) => {
                this.setState({ useCase: useCaseValue });
              }}
              dataConsumerType={this.state.dataConsumerType}
              setDataConsumerType={(dataConsumerTypeValue) => {
                this.setState({ dataConsumerType: dataConsumerTypeValue });
              }}
              formSubmitted={this.state.formSubmitted}
              onCancel={() => {
                this.setState({ redirect: Page.SEARCH });
              }}
              onSubmitAccessRequest={this.handleAccessRequest}
              loading={this.state.loading}
              requestSending={this.state.requestSending}
            />
          )}
        </SpaceBetween>
      </>
    );
  }
}
